import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Menu, X, LogOut } from "lucide-react";
import { logout } from "../../store";

import Logo from "../../assets/Transparent Logo 1.png";
import Design from "../../assets/landLord/sideBar/sidebarDesign.svg";
import Arrowup from "../../assets/icons/arrow-up.png";
import { ReactComponent as HomeIcon } from "../../assets/landLord/sideBar/home.svg";
import { ReactComponent as InsightsIcon } from "../../assets/landLord/sideBar/Insights.svg";
import { ReactComponent as InsightsIconBlue } from "../../assets/landLord/sideBar/Insights_blue.svg";
import { ReactComponent as ManageLandlordIcon } from "../../assets/landLord/sideBar/ManageLandlordIcon.svg";
import { ReactComponent as PaymentMonitoringIcon } from "../../assets/landLord/sideBar/payment.svg";
import { ReactComponent as SupportEmailIcon } from "../../assets/landLord/sideBar/SupportEmailIcon.svg";
import { ReactComponent as SupportLogIcon } from "../../assets/landLord/sideBar/SupportLogIcon.svg";
import { ReactComponent as TenantPaymentIcon } from "../../assets/landLord/sideBar/TenantPaymentIcon.svg";
import { ReactComponent as PayoutIcon } from "../../assets/landLord/sideBar/PayoutIcon.svg";
import { ReactComponent as AnalyticIcon } from "../../assets/landLord/sideBar/analytic.svg";

const menuItems = [
  {
    label: "Tenant Payments",
    icon: TenantPaymentIcon,
    path: "/landlords/tenant-payment-activity"
  },
  {
    label: "Payment Monitoring",
    icon: PaymentMonitoringIcon,
    path: "/landlords/paymonitoring",
  },
  {
    label: "Payouts",
    icon: PayoutIcon,
    path: "/landlords/admin-payout",
  },
  {
    label: "Support Emails",
    icon: SupportEmailIcon,
    path: "/landlords/support-email-settings",
  },
  {
    label: "Support Logs",
    icon: SupportLogIcon,
    path: "/landlords/support-logs",
  },
  {
    label: "Logout",
    icon: LogOut,
  },
];


const nextedMenu = [
  {
    label: "Portfolio Performance Report",
    path: "/landlords/portfolio-performance-report",
  },
  {
    label: "Occupancy rate",
    path: "/landlords/occupancy-rate",
  },
  {
    label: "Payment Distribution",
    path: "/landlords/payment-distribution",
  },
  {
    label: "Payment History",
    path: "/landlords/admin-payment-history",
  },
];

function SideBarLink({ icon: Icon, label, active, onClick, isNested = false, className = "" }) {
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 px-2 py-1 md:py-2 rounded-lg font-inter cursor-pointer transition-colors
        ${isNested
          ? "bg-transparent pl-0"
          : active
            ? "bg-[#44D7B6B8] text-dark-blue-text"
            : "text-sidebar-content"
        }
        font-normal ${className}
      `}
      style={{
        color: isNested ? (active ? "#0A124C" : "#8C8C8C") : undefined,
      }}
    >
      {Icon && <Icon className="w-4 h-4 md:w-5 md:h-5" />}
      <span className={`${isNested ? "text-[13px]" : "text-[15px]"} whitespace-nowrap`}>{label}</span>
    </div>
  );
}

export default function LandLordSideBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(() =>
    nextedMenu.some((item) => location.pathname === item.path)
  );

  // Close sidebar on navigation (mobile)
  const handleNav = (path) => {
    navigate(path);
    setOpen(false);
  };

  const toggleAnalyticsMenu = () => {
    setAnalyticsOpen(!analyticsOpen);
  };

  const isAnalyticsActive = nextedMenu.some(
    (item) => location.pathname === item.path
  );

  const handleLogout = () => {
    // Dispatch the logout action to clear auth state
    dispatch(logout());

    // Then navigate to signin page
    navigate('/signin');
  };


  return (
    <>
      {/* Hamburger for mobile */}
      <div className="fixed z-40 flex top-[18px] left-4 lg:hidden">
        <button
          className="p-2 lg:hidden focus:outline-none"
          onClick={() => setOpen(true)}
          aria-label="Open sidebar"
        >
          <Menu size={28} />
        </button>
      </div>

      {/* Overlay for mobile */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed z-40 top-0 left-0 h-full bg-background flex flex-col justify-between
          w-[330px] min-h-screen transition-transform duration-300
          ${open ? "translate-x-0" : "-translate-x-full"
          } overflow-x-hidden overflow-y-auto
          lg:translate-x-0 lg:static lg:w-[300px] lg:min-h-screen 
        `}
      >
        <div className="flex flex-col items-start flex-grow w-full max-h-screen px-6 pt-8">
          {/* Close button for mobile */}
          <div className="flex items-center justify-between w-full mb-0 relative top-[-14px] lg:hidden">
            <img
              src={Logo}
              alt="LandLod Logo"
              className="w-[100px] h-[64px] "
            />
            <button
              className="p-2"
              onClick={() => setOpen(false)}
              aria-label="Close sidebar"
            >
              <X size={24} />
            </button>
          </div>

          {/* Logo for desktop */}
          <div className="flex-col items-center hidden w-full  relative top-[-38px] mb-[-20px] lg:flex">
            <img
              src={Logo}
              alt="Tenafix Logo"
              className="w-[182px] h-[116px]"
            />
          </div>

          <div className="flex flex-col w-full gap-2 text-[15px]">

            <SideBarLink
              icon={HomeIcon}
              label="Overview"
              active={location.pathname === "/landlords/overview"}
              onClick={() => handleNav("/landlords/overview")}
            />

            <SideBarLink
              icon={location.pathname === "/landlords/insights" ? InsightsIconBlue : InsightsIcon}
              label="Insights"
              active={location.pathname === "/landlords/insights"}
              onClick={() => handleNav("/landlords/insights")}
            />

            <SideBarLink
              icon={ManageLandlordIcon}
              label="Manage Landlods"
              active={location.pathname === "/landlords/admin-manage-landload"}
              onClick={() => handleNav("/landlords/admin-manage-landload")}
            />

            <div className="flex flex-col w-[260px]">
              <div
                className={`flex items-center justify-between font-inter cursor-pointer transition-colors h-[46px] rounded-[8px]
                  ${isAnalyticsActive
                    ? "bg-[#44D7B6B8] text-dark-blue-text font-medium"
                    : "text-sidebar-content font-normal"
                  }
                `}
                style={{
                  color:
                    isAnalyticsActive
                      ? "#0A124C"
                      : "#8C8C8C",
                  padding: "11px 8px 11px 8px",
                }}
                onClick={toggleAnalyticsMenu}
              >
                <div className="flex items-center gap-[12px]">
                  <AnalyticIcon className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="text-[15px] whitespace-nowrap">
                    Analytics & Reporting
                  </span>
                </div>
                <div style={{ marginLeft: "10px" }}>
                  <img
                    src={Arrowup}
                    alt="Arrow Icon"
                    className={`max-w-[30px] max-h-[22px] transition-transform duration-300 ${analyticsOpen ? "" : "rotate-180"
                      }`}
                  />
                </div>
              </div>
              {analyticsOpen && (
                <div className="flex flex-col gap-1 mt-1 ml-4 w-[calc(100%-16px)]">
                  {nextedMenu.map((item) => (
                    <SideBarLink
                      key={item.label}
                      label={item.label}
                      active={location.pathname === item.path}
                      onClick={() => handleNav(item.path)}
                      isNested={true}
                    />
                  ))}
                </div>
              )}
            </div>
            {menuItems.filter((item) => item.label !== "Overview" && item.label !== "Logout").map((item) => (
              <SideBarLink
                key={item.label}
                icon={item.icon}
                label={item.label}
                active={location.pathname.startsWith(item.path)}
                onClick={() => handleNav(item.path)}
              />
            ))}
            
            {/* Separator */}
            {/* <div className="my-3 border-t border-gray-300" /> */}
            
            {/* Logout */}
            <SideBarLink
              icon={menuItems.find(item => item.label === "Logout")?.icon}
              label="Logout"
              active={false}
              onClick={handleLogout}
              className="bg-transparent border border-[#CECECE80] hover:border-[#b1b1b180]"
            />
          </div>
        </div>
        <div className="flex flex-col items-center">
          <p className="text-xs text-white relative top-[155px]">Powered by @2025Tenafix</p>
          <img src={Design} alt="Sidebar Design" className={`top-auto bottom-0 w-full`} />
        </div>
      </div>
    </>
  );
}
